import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatBreedingLog } from '../../types/api';

const GoatBreedingLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [logs, setLogs] = useState<GoatBreedingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/goat/breeding-logs?location_id=${selectedLocation?.id}`);
      setLogs(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load breeding logs'));
    } finally {
      setLoading(false);
    }
  };

  const updatePregnancyStatus = async (logId: string, isPregnant: boolean) => {
    try {
      setUpdatingId(logId);
      await apiClient.put(`/goat/breeding-logs/${logId}`, { pregnancy_check: isPregnant });
      await loadLogs(); // Reload logs to show updated status
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to update pregnancy status'));
    } finally {
      setUpdatingId(null);
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💑 Breeding Logs</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/breeding/new"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Record Breeding</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Logs List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Breeding Records ({logs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <span className="font-semibold text-gray-900">Doe: {log.doe_tag}</span>
                      <span className="text-gray-400">×</span>
                      <span className="font-semibold text-gray-900">Buck: {log.buck_tag}</span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Date: {new Date(log.breeding_date).toLocaleDateString()}</div>
                      <div className="flex items-center space-x-2">
                        {log.pregnancy_check ? (
                          <span className="text-green-600 font-medium flex items-center">
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Pregnancy Confirmed
                          </span>
                        ) : (
                          <span className="text-gray-500 flex items-center">
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Pregnancy Not Confirmed
                          </span>
                        )}
                        {updatingId === log.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <div className="flex items-center space-x-1 ml-2">
                            {!log.pregnancy_check && (
                              <button
                                onClick={() => updatePregnancyStatus(log.id, true)}
                                className="text-xs text-green-600 hover:text-green-700 flex items-center space-x-1 px-2 py-1 rounded hover:bg-green-50"
                                title="Mark as Pregnant"
                              >
                                <PencilIcon className="h-3 w-3" />
                                <span>Mark Positive</span>
                              </button>
                            )}
                            {log.pregnancy_check && user?.role === 'admin' && (
                              <button
                                onClick={() => updatePregnancyStatus(log.id, false)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center space-x-1 px-2 py-1 rounded hover:bg-red-50"
                                title="Mark as Not Pregnant (Admin Only)"
                              >
                                <PencilIcon className="h-3 w-3" />
                                <span>Mark Negative</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {log.expected_kidding && (
                        <div>Expected Kidding: {new Date(log.expected_kidding).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(log.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💑</div>
            <p className="text-gray-500 mb-4">No breeding records found</p>
            <Link
              to="/goat/breeding/new"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Record First Breeding
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatBreedingLogsPage;
